import { Injectable } from '@angular/core';
import { Api } from '../api.service';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ActivatedRoute } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import LogRocket from 'logrocket';
import { StewartAuthVerifierState } from '../stewart-tokens/stewart-tokens.api.service';

export type OutlookAuthVerifierState = StewartAuthVerifierState;

@UntilDestroy()
@Injectable({
	providedIn: 'root',
})
export class OutlookTokensApiService extends Api {
	code: string;
	state: string;
	constructor(
		private httpClient: HttpClient,
		private route: ActivatedRoute,
	) {
		super(httpClient);
		this.route.queryParams.pipe(untilDestroyed(this)).subscribe((params) => {
			this.code = params['code'];
			this.state = params['state'];
		});
	}

	public async getTokensFromCodeExchange() {
		const verifierStateStr = localStorage.getItem('outlook-verifier-state');

		if (!verifierStateStr) {
			throw new Error('No verifier state found in localStorage');
		}

		const verifierState: OutlookAuthVerifierState =
			JSON.parse(verifierStateStr);
		const { code_verifier: codeVerifier, state: storedState } = verifierState;

		if (!this.code) {
			throw new Error('No authorization code received');
		}

		if (!codeVerifier) {
			throw new Error('No code verifier found');
		}

		if (storedState !== this.state) {
			throw new Error('State parameter mismatch - possible CSRF attack');
		}

		LogRocket.info(
			`${OutlookTokensApiService.name}.${this.getTokensFromCodeExchange.name}: Exchanging code for token.`,
		);

		const exchange = await this.exchangeCodeForToken(
			this.code,
			codeVerifier,
			this.state,
			`${environment.outlookOAuth.authRequestRedirectUri}`,
		).toPromise();

		if (exchange.access_token) {
			localStorage.removeItem('outlook-verifier-state');
			return true;
		} else {
			localStorage.removeItem('outlook-verifier-state');
			return false;
		}
	}

	exchangeCodeForToken(
		code: string,
		codeVerifier: string,
		state: string,
		redirectUri: string,
	): Observable<{ access_token: string }> {
		const body = {
			code,
			codeVerifier,
			state,
			redirectUri,
		};

		const url = `${environment.api.baseUrl}/closer/outlook/token`;
		const response = this.httpClient
			.post(url, body, { responseType: 'text' })
			.pipe(
				map((response: string) => {
					return {
						access_token: response,
					};
				}),
			);
		return response;
	}
}
